import { Autocomplete, TextField, Typography } from "@mui/material";
import { compact, join } from "lodash-es";
import React, { forwardRef, useMemo } from "react";
import usePlacesAutocomplete, { getDetails } from "use-places-autocomplete";

import { useCountriesQuery } from "../../../queries/countries";
import { SENSITIVE_CLASS_NAME, SensitiveData } from "../../../utils/highlightUtils";
import { getAddressObject, IAddressComponents } from "./autocomplete-utils";

const EMPTY_ARRAY = [] as const;

interface Option {
    label: string;
    value: string;
    mainText: string;
    secondaryText: string;
}

export interface IGoogleAddressAutocompleteProps {
    label?: string;
    placeholder?: string;
    helperText?: string;
    disabled?: boolean;
    error?: boolean;
    inputValue?: string;
    originCountryCode?: string;
    restrictToCountry?: boolean;
    searchRadius?: number;
    onChangeInputValue?(inputValue: string): void;
    onSelect: (address: IAddressComponents) => void;
    onBlur?(): void;
}

export const GoogleAddressAutocomplete = forwardRef<HTMLInputElement, IGoogleAddressAutocompleteProps>(
    (
        {
            label,
            placeholder,
            helperText,
            disabled,
            error,
            inputValue,
            originCountryCode,
            restrictToCountry,
            searchRadius = 1.0, // 100km search radius
            onChangeInputValue,
            onSelect,
            onBlur,
        },
        ref,
    ) => {
        const { data: countries } = useCountriesQuery();

        // Add bias to search results based on the country we're searching in (if provided)
        const center = useMemo(() => {
            if (originCountryCode) {
                const country = countries?.countryByCountryCode[originCountryCode];

                if (country && country.latitude && country.longitude) {
                    return {
                        lat: country.latitude,
                        lng: country.longitude,
                    };
                }
            }
        }, [countries, originCountryCode]);

        const bounds = useMemo<google.maps.LatLngBoundsLiteral | undefined>(() => {
            if (center) {
                return {
                    north: center.lat + searchRadius,
                    south: center.lat - searchRadius,
                    east: center.lng + searchRadius,
                    west: center.lng - searchRadius,
                };
            }
        }, [center, searchRadius]);

        const {
            suggestions,
            ready,
            setValue: setAutocompleteValue,
        } = usePlacesAutocomplete({
            debounce: 300,
            cacheKey: originCountryCode,
            requestOptions: {
                bounds,
                types: ["address"],
                ...(restrictToCountry &&
                    originCountryCode && { componentRestrictions: { country: originCountryCode } }),
            },
        });

        const options: Option[] = useMemo(
            () =>
                compact([
                    ...suggestions.data.map((s) => ({
                        label: [s.structured_formatting.main_text, s.structured_formatting.secondary_text].join(", "),
                        value: s.place_id,
                        mainText: s.structured_formatting.main_text,
                        secondaryText: s.structured_formatting.secondary_text,
                    })),
                ]),
            [suggestions],
        );

        return (
            <Autocomplete
                size="small"
                options={suggestions.status === "OK" ? options : EMPTY_ARRAY}
                disabled={disabled}
                disableClearable={true}
                blurOnSelect={true}
                loading={!ready}
                freeSolo={true}
                filterOptions={(x) => x}
                filterSelectedOptions={true}
                inputValue={inputValue ?? ""}
                onInputChange={(_event, newInputValue, reason) => {
                    if (reason === "input") {
                        setAutocompleteValue(newInputValue);
                        onChangeInputValue?.(newInputValue);
                    }
                }}
                onChange={async (_event, option) => {
                    if (typeof option === "string") {
                        // We don't allow creation.
                        return;
                    }

                    const result = await getDetails({
                        placeId: option.value,
                        fields: ["address_components"],
                    });

                    if (typeof result === "object" && result.address_components) {
                        onSelect(getAddressObject(result.address_components));
                    }
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        inputRef={ref}
                        error={error}
                        label={label}
                        placeholder={placeholder}
                        helperText={helperText}
                        value={inputValue}
                        InputProps={{ ...params.InputProps, endAdornment: null }}
                        inputProps={{
                            ...params.inputProps,
                            autoComplete: "off",
                            className: join([params.inputProps.className, SENSITIVE_CLASS_NAME], " "),
                        }}
                        onBlur={onBlur}
                    />
                )}
                renderOption={(itemProps, option) => (
                    <li {...itemProps}>
                        <div>
                            <SensitiveData>
                                <Typography>{option.mainText}</Typography>
                                <Typography color="text.secondary">{option.secondaryText}</Typography>
                            </SensitiveData>
                        </div>
                    </li>
                )}
                sx={{ noOptions: { display: "none" } }}
            />
        );
    },
);
